import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useGenerateBlobSasUrlDownload } from '../../api/upload-API'
import { downloadMultipleFilesWithConcurrency } from '../../services/blobUploadService'
import { UploadTypes } from '../../utils/constants'

type GeometryChangeImage = {
  name: string
  blobPath: string
  url: string
}

type GeometryChangeImageGalleryProps = {
  images?: string[]
  gmid?: string
  runName?: string
  className?: string
}

const normalizeImageNames = (images?: string[]) => {
  if (!images) return []
  return images.filter(Boolean)
}

const getFileName = (image: string) => image.split('/').filter(Boolean).pop() || image
const geometryChangeImageCache = new Map<string, GeometryChangeImage>()

const GeometryChangeImageGallery: FC<GeometryChangeImageGalleryProps> = ({
  images,
  gmid,
  runName,
  className = 'pl-8 pr-4 pt-3'
}) => {
  const [, generateSasUrl] = useGenerateBlobSasUrlDownload()
  const generateSasUrlRef = useRef(generateSasUrl)
  const [galleryImages, setGalleryImages] = useState<GeometryChangeImage[]>([])
  const [selectedImage, setSelectedImage] = useState<GeometryChangeImage | null>(null)
  const [loading, setLoading] = useState(false)

  const imageKey = normalizeImageNames(images).join('\u0000')
  const imageNames = useMemo(() => (imageKey ? imageKey.split('\u0000') : []), [imageKey])

  const blobPaths = useMemo(() => {
    if (!gmid || !runName) return []
    return imageNames.map(image => {
      const fileName = getFileName(image)
      return `${gmid}/${runName}/${fileName}`
    })
  }, [gmid, imageNames, runName])
  const blobPathKey = blobPaths.join('\u0000')

  useEffect(() => {
    generateSasUrlRef.current = generateSasUrl
  }, [generateSasUrl])

  useEffect(() => {
    let isMounted = true
    const abortController = new AbortController()

    const loadImages = async () => {
      if (blobPaths.length === 0) {
        setGalleryImages([])
        setSelectedImage(null)
        return
      }

      const cachedImages = blobPaths
        .map(blobPath => geometryChangeImageCache.get(blobPath))
        .filter((image): image is GeometryChangeImage => !!image)
      const missingBlobPaths = blobPaths.filter(blobPath => !geometryChangeImageCache.has(blobPath))

      if (cachedImages.length > 0) {
        setGalleryImages(cachedImages)
        setSelectedImage(current => {
          if (!current) return null
          return cachedImages.find(image => image.blobPath === current.blobPath) || null
        })
      }

      if (missingBlobPaths.length === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const results = await downloadMultipleFilesWithConcurrency(
          generateSasUrlRef.current,
          UploadTypes.GEOMETRY_CHANGE_FILE,
          missingBlobPaths,
          null,
          gmid,
          abortController.signal
        )

        if (!isMounted) return

        const loadedImagesByPath = new Map(
          results
            .filter(result => result.success && result.blob)
            .map(result => {
              const url = URL.createObjectURL(result.blob as Blob)
              const image = {
                name: getFileName(result.blobName),
                blobPath: result.blobName,
                url
              }
              geometryChangeImageCache.set(result.blobName, image)
              return [result.blobName, image]
            })
        )
        const loadedImages = blobPaths
          .map(
            blobPath => geometryChangeImageCache.get(blobPath) || loadedImagesByPath.get(blobPath)
          )
          .filter((image): image is GeometryChangeImage => !!image)

        setGalleryImages(loadedImages)
        setSelectedImage(current => {
          if (!current) return null
          return loadedImages.find(image => image.blobPath === current.blobPath) || null
        })
      } catch (error) {
        if (isMounted && !abortController.signal.aborted) {
          setGalleryImages([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadImages()

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [blobPathKey, blobPaths, gmid])

  const navigateImage = (direction: 'previous' | 'next') => {
    if (!selectedImage || galleryImages.length < 2) return
    const currentIndex = galleryImages.findIndex(image => image.blobPath === selectedImage.blobPath)
    if (currentIndex < 0) return
    const nextIndex =
      direction === 'previous'
        ? (currentIndex - 1 + galleryImages.length) % galleryImages.length
        : (currentIndex + 1) % galleryImages.length
    setSelectedImage(galleryImages[nextIndex])
  }

  if (imageNames.length === 0 || (!loading && galleryImages.length === 0)) {
    return null
  }

  return (
    <div className={className}>
      <div
        className="flex items-center justify-end gap-3 overflow-x-auto pb-2"
        style={{ scrollbarGutter: 'stable' }}>
        {loading && galleryImages.length === 0 && (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
            <ProgressSpinner style={{ width: '20px', height: '20px' }} />
          </div>
        )}

        {galleryImages.map(image => (
          <button
            key={image.blobPath}
            type="button"
            className="group relative h-16 w-24 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 border-transparent bg-gray-100 transition-all hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gm-primary-90 dark:bg-gray-800"
            onClick={() => setSelectedImage(image)}>
            <img
              src={image.url}
              alt={image.name}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-end bg-black bg-opacity-0 transition-all group-hover:bg-opacity-30">
              <div className="w-full bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                <p className="truncate text-xs text-white">{image.name}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog
        visible={!!selectedImage}
        onHide={() => setSelectedImage(null)}
        header={selectedImage?.name}
        className="max-w-[95vw] max-h-[95vh]"
        style={{ width: 'auto', maxWidth: '95vw' }}
        modal
        dismissableMask>
        {selectedImage && (
          <div className="relative flex items-center justify-center">
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              className="max-h-[80vh] max-w-full object-contain"
            />

            {galleryImages.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                  onClick={() => navigateImage('previous')}>
                  <i className="pi pi-chevron-left text-base leading-none" />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                  onClick={() => navigateImage('next')}>
                  <i className="pi pi-chevron-right text-base leading-none" />
                </button>
              </>
            )}
          </div>
        )}
      </Dialog>
    </div>
  )
}

export default GeometryChangeImageGallery
